import { useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
  BaseResponsePaginationProps,
} from '@qlibs/react-components';
import ListFilterRow from '../../components/ListFilterRow';
import { readListFilter, readListPage } from '../../helpers/listFilterQuery';
import { Actions, IAction } from '@qlibs/react-components/dist/Table';
import {
  Alert,
  Button,
  Dropdown,
  Grid,
  MenuProps,
  Modal,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { httpRequest } from '../../helpers/api';
import { EDateFormat } from '../../const/config';
import { AssetProperties } from '../asset/types/asset.type';
import LinkTable from '../../components/LinkTable';
import { OperationModeProperties } from './types/operationMode.type';
import useMasterData from '../../hooks/useMasterData';
import useGroup from '../../zustand/useGroup';
import OperationModeDetail from './operationModeDetail';
import {
  RiEqualizer2Line,
  RiErrorWarningFill,
  RiMore2Line,
} from '@remixicon/react';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import {
  OperationModeMobileActionButton,
  OperationModeMobileDetailModal,
  OperationModeMobileEmptyState,
  OperationModeMobileFilterActions,
  OperationModeMobileFilterApplyButton,
  OperationModeMobileFilterButton,
  OperationModeMobileFilterDrawer,
  OperationModeMobileFilterField,
  OperationModeMobileFilterFields,
  OperationModeMobileFilterForm,
  OperationModeMobileFilterRow,
  OperationModeMobileFilterSelect,
  OperationModeMobileHeader,
  OperationModeMobileLinkText,
  OperationModeMobileMoreButton,
  OperationModeMobilePage,
  OperationModeMobilePageButton,
  OperationModeMobilePagination,
  OperationModeMobilePaginationActions,
  OperationModeMobilePaginationInfo,
  OperationModeMobileSearch,
  OperationModeMobileStatus,
  OperationModeMobileTable,
  OperationModeMobileTableWrap,
  OperationModeMobileWarningCard,
  OperationModeMobileDeviceCell,
} from './mobile';
const moment = require('moment');

const FEATURE = 'OPERATION_MODE';

const OperationMode = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();
  const { getListCompanies, companies } = useMasterData();
  const { company } = useGroup();

  const handleCreateDevice = () => {
    navigate({ pathname: '/operation-mode/add' });
  };

  const [isModalDeleteOpen, setIsModalDeleteOpen] = React.useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = React.useState(false);
  const [currentData, setCurrentData] =
    React.useState<OperationModeProperties>();
  const [forceDeleteId, setForceDeleteId] = React.useState<string>();
  const [detailModal, setModalDetail] = React.useState<boolean>(false);
  const [activeOperationMode, setActiveOperationMode] =
    React.useState<OperationModeProperties>();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
  const [mobileFilterValues, setMobileFilterValues] = React.useState({
    company: company?.companyId || 'all',
    status: 'all',
    filterData: 'default',
  });

  const {
    isLoading,
    data,
    pagination,
    query,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
  } = useFetchList<OperationModeProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'operation-mode',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      filterCompanyIds: readListFilter(
        'filterCompanyIds',
        company?.companyId || '',
      ),
      filterStatus: readListFilter('filterStatus'),
      filterArchived: readListFilter('filterArchived'),
      search: readListFilter('search'),
    },
    pageQuery: {
      defaultValue: {
        page: readListPage(),
        filterCompanyIds: '',
        filterStatus: '',
        filterArchived: '',
        search: '',
      },
    },
  });

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.operationModeId;
        await httpRequest.delete(
          '/operation-mode/' + currentData.operationModeId,
          {
            params: {
              forceDelete: forceDelete || undefined,
            },
          }
        );
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} operation mode`
        );

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch(
          '/operation-mode/' + currentData.operationModeId + '/restore'
        );
        message.success(`Success restore operation mode`);

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestoreDevice(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.operationModeId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDeleteDevice(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.operationModeId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find(
        (item) => item.operationModeId === id && item.deletedAt
      );
      if (found) {
        return true;
      }
    }

    return false;
  }

  const getOperationModeActions = (
    record: OperationModeProperties,
  ): IAction[] => {
    const isRamus2 = record.isRamus2 === true;

    return [
      isUserHasPermission([FEATURE + '.DETAIL'])
        ? {
            key: 'detailCustom',
            label: 'Detail',
            onPress: () => {
              setModalDetail(true);
              setActiveOperationMode(record);
            },
          }
        : undefined,
      !isRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])
        ? {
            key: 'update',
            label: 'Update',
            onPress: (id?: string) =>
              navigate('/operation-mode/' + id + '/edit'),
          }
        : undefined,
      !isRamus2 && isUserHasPermission([FEATURE + '.DELETE'])
        ? {
            key: 'archive',
            label: 'Delete',
            color: '#ff4d4f',
            disabled: (id?: string) => isArchived(id),
            disabledTooltip: 'This data is already archived',
            onPress: (id?: string) => handleDeleteDevice(id),
          }
        : undefined,
      !isRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])
        ? {
            key: 'restore',
            label: 'Restore',
            color: '#1890ff',
            disabled: (id?: string) => !isArchived(id),
            disabledTooltip: 'This data not in archived',
            onPress: (id?: string) => handleRestoreDevice(id),
          }
        : undefined,
      !isRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])
        ? {
            key: 'delete',
            label: 'Force Delete',
            onPress: (id?: string) => handleDeleteDevice(id, true),
          }
        : undefined,
    ].filter(Boolean) as IAction[];
  };

  const filterCompanyOptions = React.useMemo(() => {
    const data = companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
    return [...data];
  }, [companies]);

  const columns: IAppTableColumn<OperationModeProperties>[] = [
    {
      title: 'ASSET',
      dataIndex: 'asset',
      key: 'asset',
      type: 'detail',
      keyId: 'operationModeId',
      render: (value, records) => (
        <>
          <LinkTable
            onPress={() => {
              setModalDetail(true);
              setActiveOperationMode(records);
            }}
          >
            {records.deletedAt ? (
              <Tag color='#ff5500' className='mb-2'>
                Archived
              </Tag>
            ) : records.cloneData ? (
              <Tag color='#0762ff' className='mb-2'>
                Cloned
              </Tag>
            ) : (
              false
            )}
            {!records.cloneData ? records?.asset?.name : (
                <div>{records.cloneData?.name || 'N/A'}</div>
            )}
          </LinkTable>

          {records.cloneData && (
              <div style={{fontSize: 10, opacity: .5}}>Original Asset: {records?.asset?.name}</div>
          )}
        </>
      ),
    },
    {
      title: 'COMPANY',
      dataIndex: 'company',
      key: 'company',
      render: (value, records) => (
        !records.cloneData ? <div>{records?.company?.name}</div> : (
          <>
            <div>{records.cloneData?.company.name || 'N/A'}</div>
            <div style={{fontSize: 10, opacity: .5}}>Original Company: {records?.company?.name || 'N/A'}</div>
          </>
        )),
    },
    {
      title: 'DEVICE',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
      render: (value, records) => (
        <div>
          <div>{records?.device?.name}</div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>
            Device ID: {records?.device?.uniqueId}
          </div>
        </div>
      ),
    },
    {
      title: 'CONSTANT',
      dataIndex: 'operationModeDetails',
      key: 'operationModeDetails',
      render: (value, records) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div>{records?.operationModeDetails?.length}</div>
          {records?.operationModeDetails?.length === 0 ? (
            <Tooltip title='Constant must be added.'>
              <RiErrorWarningFill color='red' size={20} />
            </Tooltip>
          ) : (
            false
          )}
        </div>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'START AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, records) =>
        moment(records.startAt).format('DD MMMM YYYY'),
    },
    {
      title: 'ACTION',
      key: 'action',
      render: (_, record) => {
        const actions = getOperationModeActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.operationModeId} actions={actions} />;
      },
    },
  ];

  const filterData: any = [
    {
      type: 'select',
      key: 'company',
      label: 'Company',
      options: filterCompanyOptions,
      value: pageQueries.filterCompanyIds || 'All',
      onChange: (value: any) => {
        filterDropdown({
          filterCompanyIds: value === 'all' ? '' : value,
        });
      },
      colSpan: 8,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
    },
  ].filter((item) =>
    !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item
  );

  useEffect(() => {
    getListCompanies();
  }, []);

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      company: pageQueries.filterCompanyIds || company?.companyId || 'all',
      status:
        pageQueries.filterStatus || pageQueries.status || 'all',
      filterData:
        pageQueries.filterArchived || pageQueries.filterData || 'default',
    });
  }, [company?.companyId, isMobileFilterOpen, pageQueries]);

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  const handleApplyMobileFilter = () => {
    filterDropdown({
      filterCompanyIds:
        mobileFilterValues.company === 'all' ? '' : mobileFilterValues.company,
      filterStatus:
        mobileFilterValues.status === 'all' ? '' : mobileFilterValues.status,
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    });
    setIsMobileFilterOpen(false);
  };

  const getMobileActionItems = (
    record: OperationModeProperties,
  ): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (isUserHasPermission([FEATURE + '.DETAIL'])) {
      items.push({ key: 'detail', label: 'Detail' });
    }

    if (!record.isRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])) {
      items.push({ key: 'update', label: 'Update' });
    }

    if (!record.isRamus2 && isUserHasPermission([FEATURE + '.DELETE'])) {
      items.push({
        key: 'archive',
        label: 'Delete',
        danger: true,
        disabled: isArchived(record.operationModeId),
      });
    }

    if (!record.isRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])) {
      items.push({
        key: 'restore',
        label: 'Restore',
        disabled: !isArchived(record.operationModeId),
      });
    }

    if (!record.isRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])) {
      items.push({ key: 'delete', label: 'Force Delete', danger: true });
    }

    return items;
  };

  const handleMobileActionClick =
    (record: OperationModeProperties) =>
    (info: MenuProps['onClick'] extends (arg: infer I) => any ? I : any) => {
      if (info?.key === 'detail') {
        setModalDetail(true);
        setActiveOperationMode(record);
      }

      if (info?.key === 'update') {
        navigate('/operation-mode/' + record.operationModeId + '/edit');
      }

      if (info?.key === 'archive') {
        handleDeleteDevice(record.operationModeId);
      }

      if (info?.key === 'restore') {
        handleRestoreDevice(record.operationModeId);
      }

      if (info?.key === 'delete') {
        handleDeleteDevice(record.operationModeId, true);
      }
    };

  return (
    <React.Fragment>
      {isMobile ? (
        <OperationModeMobilePage>
          <OperationModeMobileHeader
            title="Operation Mode"
            onBack={() => navigate(-1)}
            action={
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <OperationModeMobileActionButton
                  type="primary"
                  onClick={handleCreateDevice}
                >
                  Add
                </OperationModeMobileActionButton>
              ) : undefined
            }
          />

          <OperationModeMobileFilterRow>
            <OperationModeMobileSearch
              allowClear
              placeholder="Search by operation mode"
              defaultValue={pageQueries.search}
              suffix={<SearchOutlined />}
              onChange={(event) => handleSearch(event.target.value)}
            />
            <OperationModeMobileFilterButton
              type="button"
              aria-label="Open operation mode filters"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <RiEqualizer2Line />
            </OperationModeMobileFilterButton>
          </OperationModeMobileFilterRow>

          {isLoading ? (
            <OperationModeMobileEmptyState>
              Loading operation modes...
            </OperationModeMobileEmptyState>
          ) : !data.length ? (
            <OperationModeMobileEmptyState>
              No operation mode data found.
            </OperationModeMobileEmptyState>
          ) : (
            <OperationModeMobileTableWrap>
              <OperationModeMobileTable $minWidth={1384}>
                <thead>
                  <tr>
                    <th style={{ width: 220 }}>Asset</th>
                    <th style={{ width: 320 }}>Company</th>
                    <th style={{ width: 200 }}>Device</th>
                    <th style={{ width: 182 }}>Constant</th>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 182 }}>Start at</th>
                    <th style={{ width: 142 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => (
                    <tr key={item.operationModeId}>
                      <td>
                        <OperationModeMobileLinkText
                          type="button"
                          onClick={() => {
                            setModalDetail(true);
                            setActiveOperationMode(item);
                          }}
                        >
                          {item.deletedAt ? (
                            <Tag color="#ff5500" className="mb-2">
                              Archived
                            </Tag>
                          ) : null}
                          {item.asset?.name || 'N/A'}
                        </OperationModeMobileLinkText>
                      </td>
                      <td>{item.company?.name || 'N/A'}</td>
                      <td>
                        <OperationModeMobileDeviceCell>
                          <strong>{item.device?.name || 'N/A'}</strong>
                          <span>Device ID: {item.device?.uniqueId || '-'}</span>
                        </OperationModeMobileDeviceCell>
                      </td>
                      <td>{item.operationModeDetails?.length || 0}</td>
                      <td>
                        <OperationModeMobileStatus
                          $active={item.status === 'active'}
                        >
                          {item.status || 'inactive'}
                        </OperationModeMobileStatus>
                      </td>
                      <td>
                        {item.startAt
                          ? moment(item.startAt).format('DD MMM YYYY, HH:mm')
                          : 'N/A'}
                      </td>
                      <td>
                        <Dropdown
                          trigger={['click']}
                          menu={{
                            items: getMobileActionItems(item),
                            onClick: handleMobileActionClick(item),
                          }}
                        >
                          <OperationModeMobileMoreButton
                            type="button"
                            aria-label={`Open actions for ${
                              item.asset?.name || 'operation mode'
                            }`}
                          >
                            <RiMore2Line />
                          </OperationModeMobileMoreButton>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </OperationModeMobileTable>
            </OperationModeMobileTableWrap>
          )}

          <OperationModeMobilePagination>
            <OperationModeMobilePaginationInfo>
              Showing {mobileStartResult} to {mobileEndResult} of {mobileTotal}{' '}
              results
            </OperationModeMobilePaginationInfo>
            <OperationModeMobilePaginationActions>
              <OperationModeMobilePageButton
                type="button"
                disabled={mobileCurrentPage <= 1}
                onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
              >
                <LeftOutlined />
              </OperationModeMobilePageButton>
              <OperationModeMobilePageButton type="button" $active>
                {mobileCurrentPage}
              </OperationModeMobilePageButton>
              <OperationModeMobilePageButton
                type="button"
                disabled={mobileCurrentPage >= mobileTotalPages}
                onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
              >
                <RightOutlined />
              </OperationModeMobilePageButton>
            </OperationModeMobilePaginationActions>
          </OperationModeMobilePagination>
        </OperationModeMobilePage>
      ) : (
        <>
          <HeaderSection
            title={'Operation Mode'}
            rightAction={[
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <Button type='primary' onClick={handleCreateDevice}>
                  Add Operation Mode
                </Button>
              ) : (
                false
              ),
            ]}
          />

          <ListFilterRow
            filterValues={{
              search: pageQueries.search,
              status: pageQueries.filterStatus || 'all',
              filterData: pageQueries.filterArchived || 'default',
              company: company.name || 'all',
            }}
            filters={[filterData]}
          />

          <AppTable
            isLoading={isLoading}
            keyId='operationModeId'
            columns={columns}
            data={data}
            pagination={pagination}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
            // _table={{
            //   scroll:{ x: '100%' }
            // } as any}
          />
        </>
      )}

      {isMobile && (
        <OperationModeMobileFilterDrawer
          title="Filter"
          placement="bottom"
          height="auto"
          open={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        >
          <OperationModeMobileFilterForm>
            <OperationModeMobileFilterFields>
              <OperationModeMobileFilterField>
                Company
                <OperationModeMobileFilterSelect
                  value={mobileFilterValues.company}
                  options={[
                    { value: 'all', label: 'All' },
                    ...filterCompanyOptions,
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      company: String(value),
                    }))
                  }
                />
              </OperationModeMobileFilterField>
              <OperationModeMobileFilterField>
                Status
                <OperationModeMobileFilterSelect
                  value={mobileFilterValues.status}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      status: String(value),
                    }))
                  }
                />
              </OperationModeMobileFilterField>
              {isUserHasPermission([FEATURE + '.DELETE']) && (
                <OperationModeMobileFilterField>
                  Filter Data
                  <OperationModeMobileFilterSelect
                    value={mobileFilterValues.filterData}
                    options={[
                      { value: 'default', label: 'Default' },
                      { value: 'show', label: 'Include Archive Data' },
                    ]}
                    onChange={(value) =>
                      setMobileFilterValues((prev) => ({
                        ...prev,
                        filterData: String(value),
                      }))
                    }
                  />
                </OperationModeMobileFilterField>
              )}
            </OperationModeMobileFilterFields>
            <OperationModeMobileFilterActions>
              <OperationModeMobileFilterApplyButton
                type="primary"
                onClick={handleApplyMobileFilter}
              >
                Apply
              </OperationModeMobileFilterApplyButton>
            </OperationModeMobileFilterActions>
          </OperationModeMobileFilterForm>
        </OperationModeMobileFilterDrawer>
      )}

      {activeOperationMode && isMobile && (
        <OperationModeMobileDetailModal
          title={`Operation Mode ${activeOperationMode.company?.name}`}
          open={detailModal}
          width="calc(100vw - 24px)"
          onCancel={() => {
            setModalDetail(false);
            setActiveOperationMode(undefined);
          }}
          footer={''}
        >
          <OperationModeMobileWarningCard>
            <div>
              <strong>Device Name:</strong>{' '}
              {activeOperationMode.device?.name || '-'}
            </div>
            <div>
              <strong>Device ID:</strong>{' '}
              {activeOperationMode.device?.uniqueId || '-'}
            </div>
          </OperationModeMobileWarningCard>
          <OperationModeDetail
            operationModeId={activeOperationMode.operationModeId}
            ramus2AssetId={activeOperationMode.assetId}
            isRamus2={activeOperationMode.isRamus2 === true}
            operationModeTitle={`Operation Mode ${
              activeOperationMode.company?.name || ''
            }`}
          />
        </OperationModeMobileDetailModal>
      )}

      {activeOperationMode && !isMobile && (
        <Modal
          title={`Operation Mode ${activeOperationMode.company?.name}`}
          open={detailModal}
          width={900}
          onCancel={() => {
            setModalDetail(false);
            setActiveOperationMode(undefined);
          }}
          footer={''}
        >
          <Alert
            style={{ marginBottom: 10, marginTop: 10 }}
            message={
              <div style={{ display: 'flex', gap: 20 }}>
                <div>
                  <b>Device Name:</b> {activeOperationMode.device?.name || '-'}
                </div>
                <div>
                  <b>Device ID:</b>{' '}
                  {activeOperationMode.device?.uniqueId || '-'}
                </div>
              </div>
            }
            type='warning'
          />
          <OperationModeDetail
            operationModeId={activeOperationMode.operationModeId}
            ramus2AssetId={activeOperationMode.assetId}
            isRamus2={activeOperationMode.isRamus2 === true}
            operationModeTitle={`Operation Mode ${
              activeOperationMode.company?.name || ''
            }`}
          />
        </Modal>
      )}

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title='Restore Confirmation'
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>
    </React.Fragment>
  );
};
export default OperationMode;
